// Libro de tarjetas PCF de varias tiendas: una lectura por tienda por vuelta; una tienda sin lotes no falla.
import { useInfiniteQuery, type InfiniteData } from '@tanstack/react-query';
import { getCardStock } from '@/api/services/retail-cards/retail-cards.client';
import { isStockNotFound } from '@/domains/retail/tarjetas/api/useRetailCards';
import { nextCardRound, type CardLedgerRound, type CardStoreRead } from '../lib/inventoryLedger';
import { CARD_STOCK_QUERY_KEY } from './useInventarioLens';

/** Movimientos por tienda en cada vuelta. */
const CARD_LEDGER_PAGE_SIZE = 25;

/** `null` = primera vuelta (todas las tiendas); si no, el cursor de cada tienda con más páginas. */
type RoundParam = Record<string, string> | null;

async function readStore(
    retailerId: string,
    storeId: string,
    startKey: string | undefined,
): Promise<CardStoreRead> {
    try {
        const page = await getCardStock(retailerId, storeId, {
            limit: CARD_LEDGER_PAGE_SIZE,
            startKey,
        });
        return {
            storeId,
            balance: page.balance,
            movements: page.movements,
            nextToken: page.nextToken,
        };
    } catch (error) {
        if (isStockNotFound(error)) return { storeId, balance: null, movements: [] };
        throw error;
    }
}

export function useCardLedger(retailerId: string | null, storeIds: readonly string[]) {
    return useInfiniteQuery<
        CardLedgerRound,
        Error,
        InfiniteData<CardLedgerRound>,
        readonly unknown[],
        RoundParam
    >({
        queryKey: [...CARD_STOCK_QUERY_KEY, 'ledger', retailerId, storeIds],
        initialPageParam: null,
        queryFn: async ({ pageParam }) => {
            const targets: [string, string | undefined][] =
                pageParam === null
                    ? storeIds.map((id) => [id, undefined])
                    : Object.entries(pageParam);
            const stores = await Promise.all(
                targets.map(([storeId, startKey]) =>
                    readStore(retailerId ?? '', storeId, startKey),
                ),
            );
            return { stores };
        },
        getNextPageParam: (last) => nextCardRound(last),
        enabled: retailerId !== null && storeIds.length > 0,
        retry: false,
    });
}
